import { useMemo, useReducer, useState } from 'react';
import Layout from '@/components/UI/StepForm/Layout';
import { type Step, type StepFormReducer, type StepFormState } from '@/components/UI/StepForm//StepForm.model';
import { reducer } from '@/components/UI/StepForm/StepForm.reducer.ts';
import Checkbox from '@/components/UI/FormElements/Checkbox';

interface StepFormProps<T extends StepFormState<T>, PropType extends Record<string, any>> {
  title: string;
  steps: Array<Step<T, PropType>>;
  initialState: T;
  props: PropType;
  finalStepAction: (state: T, submitMapping: boolean) => void;
  horizontal: boolean;
  hasPlannedAnimals: boolean;
}

const RandomizationStepForm = <T extends StepFormState<T>, PropType extends Record<string, any>>({
  title,
  steps,
  initialState,
  props,
  finalStepAction,
  horizontal,
  hasPlannedAnimals,
}: StepFormProps<T, PropType>): JSX.Element => {
  const [state, dispatch] = useReducer<StepFormReducer<T>>(reducer, initialState);
  const [enableMapping, setEnableMapping] = useState<boolean>(false);
  const nextStep = () => dispatch({ type: 'next' });
  const previousStep = () => dispatch({ type: 'previous' });
  const jumpToStep = (currentStep: number) => dispatch({ type: 'jumpToStep', currentStep });
  const { currentStep, stepReady, finalStepReady = true } = state;
  const step = steps[currentStep];
  const errorMessage = step.isValid(state);
  const isValid = errorMessage === true;

  const enableNextStep = useMemo(() => {
    if (hasPlannedAnimals && step.name === 'Randomize') {
      return enableMapping;
    } else {
      return state.currentStep !== steps.length - 1;
    }
  }, [enableMapping, step.name, state.currentStep, steps.length]);

  return (
    <div className="h-100 flex flex-column justify-between">
      <Layout
        title={title}
        steps={steps}
        currentStep={currentStep}
        jumpToStep={jumpToStep}
        horizontal={horizontal}
        content={step.renderStep(state, dispatch, props)}
        state={state}
        dispatch={dispatch}
        testPrefix={'ranomization-stepform'}
        titlePadding={undefined}
        errorBanner={undefined}
      />

      {
        <div className="flex justify-between mt3">
          <div className={`flex justify-start`}>
            <button
              disabled={state.currentStep === 0}
              className="plain mr2"
              onClick={previousStep}
              style={{ minWidth: '95px' }}
            >
              Back
            </button>

            {enableNextStep ? (
              <div
                data-tooltip-id="global-tooltip-id"
                data-tooltip-content={typeof errorMessage === 'string' ? errorMessage : undefined}
                data-tooltip-hidden={isValid}
              >
                <button
                  className="mr2"
                  disabled={!isValid || !(stepReady ?? true)}
                  onClick={nextStep}
                  style={{ minWidth: '95px' }}
                >
                  Next
                </button>
              </div>
            ) : (
              <button
                className="mr2"
                disabled={!isValid || !finalStepReady}
                onClick={() => finalStepAction(state, enableMapping)}
                style={{ minWidth: '95px' }}
              >
                Finish
              </button>
            )}
            {step.name === 'Randomize' && hasPlannedAnimals && (
              <div className="ml3 flex items-center">
                <Checkbox
                  id={'proceed-to-mapping'}
                  name={'proceedToMapping'}
                  label={'Proceed to mapping stage'}
                  onChange={() =>
                    setEnableMapping((prev) => {
                      return !prev;
                    })
                  }
                  checked={enableMapping}
                />
              </div>
            )}
          </div>
        </div>
      }
    </div>
  );
};

export default RandomizationStepForm;
